import { faBars } from '@fortawesome/pro-regular-svg-icons/faBars';
import { faSearch } from '@fortawesome/pro-regular-svg-icons/faSearch';
import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes';
import PNLogoWhite from '@propertypal/shared/src/resources/logos/property-news-logo-white.svg?url';
import PNLogo from '@propertypal/shared/src/resources/logos/property-news-logo.svg?url';
import ContentHubSearchResults from '@propertypal/web-ui/src/content-hub/ContentHubSearchResults';
import useSearchQuery, { SEARCH_QUERY_PARAM } from '@propertypal/web-ui/src/hooks/useSearchQuery';
import Chevron from '@propertypal/web-ui/src/icons/Chevron';
import FontAwesomeIcon from '@propertypal/web-ui/src/icons/FontAwesomeIcon';
import PageWrapper from '@propertypal/web-ui/src/layout/PageWrapper';
import { MidHeading, Text } from '@propertypal/web-ui/src/typography';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import togglePageScroll from '../../utils/togglePageScroll';
import { LinksContainer, Anchor } from '../footer/copyright-and-policy-links/CopyrightAndPolicyLinks.style';
import { FooterInner, LinksColumn } from '../footer/Footer.style';
import SocialLinks from '../footer/social-links/SocialLinks';
import {
  Container,
  CopyrightContainer,
  CopyrightWrapper,
  FooterContainer,
  FooterLinks,
  FooterLogo,
  Header,
  HeaderContent,
  HeaderPanel,
  LinksRow,
  LogoPN,
  Menu,
  MenuButton,
  MenuLink,
  MenuLinks,
  NavBox,
  SearchInput,
  FooterLinkGrid,
} from './BlogLayout.style.pn';

interface Props {
  children?: React.ReactNode;
}

const BlogLayout: FunctionComponent<Props> = (props) => {
  const theme = useTheme();
  const router = useRouter();
  const searchParams = useSearchParams();
  const { searchText, setSearchText } = useSearchQuery();
  // check whether there is a faq search query
  const searchQuery = searchParams.get(SEARCH_QUERY_PARAM);

  const [, setProfileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const headerRef = useRef<HTMLDivElement | null>(null);

  const toggleMoreMenu = () => {
    setMenuOpen(!menuOpen);
    togglePageScroll('#blogMenu', menuOpen);
  };

  //   const updateProfileOpen = (open: boolean) => {
  //     setProfileOpen(open);
  //   };

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      // @ts-ignore
      if (headerRef.current && !headerRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };

    const handleRouteChange = () => {
      // close menu on route.
      setMenuOpen(false);
      togglePageScroll('#blogMenu', true);
    };

    const handleRouteChangeComplate = (url: string) => {
      if (typeof window !== 'undefined') {
        const { searchParams: params } = new URL(url, window.location.origin);
        if (!params.get(SEARCH_QUERY_PARAM)) setSearchText('');
      }
    };

    document.addEventListener('click', handleClick);
    router.events.on('routeChangeStart', handleRouteChange);
    router.events.on('routeChangeComplete', handleRouteChangeComplate);

    return () => {
      document.removeEventListener('click', handleClick);
      router.events.off('routeChangeStart', handleRouteChange);
      router.events.off('routeChangeComplete', handleRouteChangeComplate);
    };
  }, []);

  return (
    <>
      <Container>
        <Header data-testid="blog-layout">
          <HeaderContent ref={headerRef} $menuOpen={menuOpen}>
            {!menuOpen && (
              <HeaderPanel>
                <Link href="/" prefetch={false}>
                  <Text fontSize={10}>
                    <Chevron direction="left" size={8} /> Back to
                  </Text>
                  <LogoPN src={PNLogo.src} />
                </Link>

                <LinksRow>
                  <NavBox
                    href="/news"
                    prefetch={false}
                    $current={router?.pathname === '/news' || router.asPath.includes('/news/')}
                  >
                    News
                  </NavBox>
                </LinksRow>
              </HeaderPanel>
            )}

            <HeaderPanel>
              <SearchInput menuOpen={menuOpen}>
                <FontAwesomeIcon icon={faSearch} color={theme.primary} />

                <input
                  type="search"
                  value={searchText}
                  onChange={(event) => setSearchText(event.target.value)}
                  aria-label="News Search"
                  placeholder="Search..."
                  data-testid="blog-search-input"
                />
              </SearchInput>

              {/* <ProfileContainer data-testid="profile-container">
                <ProfileDropdown
                  open={profileOpen}
                  setOpen={updateProfileOpen}
                  textColor={theme.textDark}
                  bgColor="#222"
                />
              </ProfileContainer> */}

              <MenuButton onClick={toggleMoreMenu}>
                {menuOpen ? (
                  <>
                    <FontAwesomeIcon icon={faTimes} color={theme.textDark} size={20} />
                    <span>Close</span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faBars} color={theme.textDark} size={20} />
                    <span>Menu</span>
                  </>
                )}
              </MenuButton>
            </HeaderPanel>
          </HeaderContent>
        </Header>

        <Menu id="blogMenu" data-testid="blog-menu" style={{ display: menuOpen ? 'flex' : 'none' }}>
          <MenuLinks>
            <MenuLink href="/news" $current={router?.pathname === '/news' || router.asPath.includes('/news/')}>
              News
            </MenuLink>
          </MenuLinks>

          <Link
            href="/"
            style={{ padding: 15, display: 'block', borderTop: `1px solid ${theme.textLight}` }}
            prefetch={false}
          >
            <Text fontSize={10} color={theme.white}>
              <Chevron direction="left" size={8} /> Back to
            </Text>

            <LogoPN src={PNLogoWhite.src} />
          </Link>
        </Menu>

        {/* if search query active, render search markup */}
        {searchQuery ? <ContentHubSearchResults searchQuery={searchQuery} homeLink="/news" /> : props.children}
      </Container>
      <FooterContainer className="hide-print">
        <PageWrapper>
          <FooterInner data-testid="faq-footer">
            <FooterLinks>
              <LinksColumn>
                <Link href="/" prefetch={false}>
                  <Text as="span" fontSize={11} mb={10} display="block">
                    <Chevron direction="left" size={8} /> Back to
                  </Text>
                  <ul>
                    <FooterLogo src={PNLogo.src} alt="Propertynews Logo" />
                  </ul>
                </Link>
              </LinksColumn>
            </FooterLinks>
            <div>
              <MidHeading fontSize={17} mb={10}>
                FIND US ON SOCIAL
              </MidHeading>
              <SocialLinks />
            </div>
          </FooterInner>

          <FooterLinkGrid>
            <Link href="/news" prefetch={false}>
              News
            </Link>
            <Link href="/search" prefetch={false}>
              Find a Property
            </Link>
            <div className="placeholder" />
            <Link href="/faq" prefetch={false}>
              FAQ
            </Link>
            <Link href="/contact" prefetch={false}>
              Contact Us
            </Link>
            <div className="placeholder" />
          </FooterLinkGrid>
        </PageWrapper>

        <CopyrightWrapper>
          <CopyrightContainer data-testid="footer-copyright">
            <Text fontSize={14} color={theme.textDark}>
              &copy; Copyright Propertynews.com {new Date().getFullYear()}
            </Text>

            <LinksContainer>
              <Anchor href="/privacy" prefetch={false}>
                <Text fontSize={14} color={theme.textDark}>
                  Privacy Policy
                </Text>
              </Anchor>

              <Anchor href="/cookie-policy" prefetch={false}>
                <Text fontSize={14} color={theme.textDark}>
                  Cookie Policy
                </Text>
              </Anchor>

              <Anchor href="/terms" prefetch={false}>
                <Text fontSize={14} color={theme.textDark}>
                  Terms &amp; Conditions
                </Text>
              </Anchor>
            </LinksContainer>
          </CopyrightContainer>
        </CopyrightWrapper>
      </FooterContainer>
    </>
  );
};

export default BlogLayout;
